import React, { useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Grid,
  Paper,
  Badge
} from '@mui/material';
import {
  Person,
  Group,
  SupervisorAccount,
  Business,
  LocationOn,
  LocalHospital
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTeamHierarchy } from '../../store/slices/usersSlice';
import LoadingSpinner from '../common/LoadingSpinner';

const TeamHierarchy = () => {
  const dispatch = useDispatch();
  const { teamHierarchy, loading } = useSelector((state) => state.users);
  const { user: currentUser } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(fetchTeamHierarchy());
  }, [dispatch]);

  if (loading) {
    return <LoadingSpinner message="Loading team hierarchy..." />;
  }

  if (!teamHierarchy) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="text.secondary" align="center">
            No team data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const renderUserCard = (user, role) => (
    <Card key={user.id} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: getAvatarColor(role), mr: 2 }}>
            {getUserIcon(role)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">
              {user.first_name} {user.last_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              @{user.username}
            </Typography>
            <Chip 
              label={role.replace('_', ' ').toUpperCase()} 
              size="small" 
              color="primary" 
              sx={{ mt: 0.5 }}
            />
          </Box>
          {user.phone && (
            <Typography variant="body2" color="text.secondary">
              📞 {user.phone}
            </Typography>
          )}
        </Box>

        {/* Cities and Diseases */}
        {(user.assigned_cities || user.assigned_diseases) && (
          <Box sx={{ mb: 2 }}>
            {user.assigned_cities && user.assigned_cities.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <LocationOn fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Cities:
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {user.assigned_cities.map((city, index) => (
                    <Chip
                      key={index}
                      label={typeof city === 'string' ? city : city.name || city}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {user.assigned_diseases && user.assigned_diseases.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <LocalHospital fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    Specializations:
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {user.assigned_diseases.map((disease, index) => (
                    <Chip
                      key={index}
                      label={typeof disease === 'string' ? disease : disease.name || disease}
                      size="small"
                      variant="outlined"
                      color="success"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const getAvatarColor = (role) => {
    switch (role) {
      case 'admin': return 'error.main';
      case 'manager': return 'error.main';
      case 'assistant_manager': return 'secondary.main';
      case 'team_lead': return 'warning.main';
      case 'bda': return 'info.main';
      default: return 'primary.main';
    }
  };

  const getUserIcon = (role) => {
    switch (role) {
      case 'admin': return <Business />;
      case 'manager': return <Business />;
      case 'assistant_manager': return <SupervisorAccount />;
      case 'team_lead': return <Group />;
      case 'bda': return <Person />;
      default: return <Person />;
    }
  };

  // For assistant managers, show their team structure
  if (teamHierarchy.assistant_manager) {
    const { assistant_manager } = teamHierarchy;

    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Group color="primary" />
          Team Hierarchy
        </Typography>

        {/* Assistant Manager Card */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'secondary.50' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupervisorAccount color="secondary" />
            Assistant Manager
          </Typography>
          {renderUserCard(assistant_manager, 'assistant_manager')}
        </Paper>

        {/* Team Leads and their BDAs */}
        {assistant_manager.team_leads && assistant_manager.team_leads.length > 0 && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'warning.50' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group color="warning" />
              Team Leads ({assistant_manager.team_leads.length})
            </Typography>

            <Grid container spacing={2}>
              {assistant_manager.team_leads.map((teamLead) => (
                <Grid item xs={12} md={6} key={teamLead.id}>
                  <Box sx={{ border: '1px solid', borderColor: 'warning.light', borderRadius: 1, p: 2 }}>
                    {renderUserCard(teamLead, 'team_lead')}

                    {/* BDAs under this team lead */}
                    {teamLead.bdas && teamLead.bdas.length > 0 && (
                      <Box sx={{ mt: 2, pl: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person fontSize="small" color="info" />
                          Team Members ({teamLead.bdas.length})
                        </Typography>
                        {teamLead.bdas.map((bda) => renderUserCard(bda, 'bda'))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {(!assistant_manager.team_leads || assistant_manager.team_leads.length === 0) && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Group sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No team leads assigned yet
            </Typography>
          </Paper>
        )}
      </Box>
    );
  }

  // For managers, show their team structure
  if (teamHierarchy.manager) {
    const { manager } = teamHierarchy;

    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Group color="primary" />
          Team Hierarchy
        </Typography>

        {/* Manager Card */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.50' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business color="error" />
            Manager
          </Typography>
          {renderUserCard(manager, 'manager')}
        </Paper>

        {/* Assistant Managers and their Team Leads → BDAs */}
        {manager.assistant_managers && manager.assistant_managers.length > 0 && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'secondary.50' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupervisorAccount color="secondary" />
              Assistant Managers ({manager.assistant_managers.length})
            </Typography>

            {manager.assistant_managers.map((am) => (
              <Box key={am.id} sx={{ border: '1px solid', borderColor: 'secondary.light', borderRadius: 1, p: 2, mb: 2 }}>
                {renderUserCard(am, 'assistant_manager')}

                {/* Team Leads under this Assistant Manager */}
                {am.team_leads && am.team_leads.length > 0 && (
                  <Box sx={{ mt: 2, pl: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Group fontSize="small" color="warning" />
                      Team Leads ({am.team_leads.length})
                    </Typography>

                    <Grid container spacing={2}>
                      {am.team_leads.map((teamLead) => (
                        <Grid item xs={12} md={6} key={teamLead.id}>
                          <Box sx={{ border: '1px solid', borderColor: 'warning.light', borderRadius: 1, p: 2 }}>
                            {renderUserCard(teamLead, 'team_lead')}

                            {/* BDAs under this Team Lead */}
                            {teamLead.bdas && teamLead.bdas.length > 0 && (
                              <Box sx={{ mt: 2, pl: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Person fontSize="small" color="info" />
                                  Team Members ({teamLead.bdas.length})
                                </Typography>
                                {teamLead.bdas.map((bda) => renderUserCard(bda, 'bda'))}
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Box>
            ))}
          </Paper>
        )}

        {/* Direct Team Leads (not under any assistant manager) */}
        {manager.team_leads && manager.team_leads.length > 0 && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'warning.50' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupervisorAccount color="warning" />
              Team Leads ({manager.team_leads.length})
            </Typography>

            <Grid container spacing={2}>
              {manager.team_leads.map((teamLead) => (
                <Grid item xs={12} md={6} key={teamLead.id}>
                  <Box sx={{ border: '1px solid', borderColor: 'warning.light', borderRadius: 1, p: 2 }}>
                    {renderUserCard(teamLead, 'team_lead')}

                    {teamLead.bdas && teamLead.bdas.length > 0 && (
                      <Box sx={{ mt: 2, pl: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Person fontSize="small" color="info" />
                          Team Members ({teamLead.bdas.length})
                        </Typography>
                        {teamLead.bdas.map((bda) => renderUserCard(bda, 'bda'))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Direct BDAs (not under any team lead) */}
        {teamHierarchy.direct_bdas && teamHierarchy.direct_bdas.length > 0 && (
          <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person color="info" />
              Direct BDAs ({teamHierarchy.direct_bdas.length})
            </Typography>

            <Grid container spacing={2}>
              {teamHierarchy.direct_bdas.map((bda) => (
                <Grid item xs={12} sm={6} md={4} key={bda.id}>
                  {renderUserCard(bda, 'bda')}
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}
      </Box>
    );
  }

  // For team leads, show their team members
  if (teamHierarchy.team_lead) {
    const { team_lead } = teamHierarchy;
    
    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Group color="primary" />
          My Team
        </Typography>
        
        {/* Team Lead Info */}
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'warning.50' }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupervisorAccount color="warning" />
            Team Lead
          </Typography>
          {renderUserCard(team_lead, 'team_lead')}
        </Paper>

        {/* Team Members */}
        {team_lead.bdas && team_lead.bdas.length > 0 && (
          <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group color="info" />
              Team Members ({team_lead.bdas.length})
            </Typography>
            
            <Grid container spacing={2}>
              {team_lead.bdas.map((bda) => (
                <Grid item xs={12} sm={6} md={4} key={bda.id}>
                  {renderUserCard(bda, 'bda')}
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {(!team_lead.bdas || team_lead.bdas.length === 0) && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Group sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              No team members assigned yet
            </Typography>
          </Paper>
        )}
      </Box>
    );
  }

  // Fallback for other roles or empty data
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" color="text.secondary" align="center">
          No team hierarchy data available
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TeamHierarchy;